<template>
  <div class="container-fluid page top">
    <div class="row row1 col-md-12" ref="refGeneralSearchBarRow">
      <MovieSearchBar :devices="carState.devices" />
    </div>
    <div class="row row2 col-md-12">
      <Multipane class="vertical-panes" @paneResizeStop="paneResizeStopped">
        <div class="pane-left" :style="{
            'min-width': styles.paneStyles.sideMinWidth,
            'max-width': styles.paneStyles.sideMaxWidth,
          }" ref="refPaneLeft"
        >
          <TopLeftPane />
        </div>
        <MultipaneResizer />
        <div class="pane-center" :style="{
            'min-width': styles.paneStyles.centerMinWidth,
            'max-width': styles.paneStyles.centerMaxWidth,
          }" ref="refPaneCenter"
        >
          <div class="misc-info" ref="refMapTopMiscBar">
            <div class="misc-info-left" ref="refMapTopMiscBarLeft">
            </div>
            <div class="misc-info-right ta-right" ref="refMapTopMiscBarRight">
              <div class="car-info-row">
                <div class="car-info-data-age" v-if="!!carState.carsUpdatedAt">
                  <span class="ib">
                    車両データ取得時刻: {{dtFormat(carState.carsUpdatedAt, 'yyyy/mm/dd HH:MM:SS')}}
                  </span>
                </div>
              </div>
              <div class="spacer-row"></div>
            </div>
          </div>
          <ExtremeMap
            v-show="isMapReady"
            ref="refExtremeMap"
            :map-essentials="state.extremeMapEssentials"
            :disable-movie-layer-interaction="false"
            :should-fit-initial-extent="state.shouldFitInitialExtent"
            @click-map="onClickMap"
            @click-item="onMapItemClicked"
            @all-deselected="onMapAllDeselected"
            @zoom-changed="onMapZoomChanged"
          />

          <div class="map-selected-elem-info-area mt4"
            :class="{ hide: !mapElem.mapSelectedElemInfo }"
            ref="refMapSelectedElemInfoArea"
          >
            <keep-alive>
              <component
                :is="currentElemInfoComponent"
                :elem="mapElem.mapSelectedElemInfo"
                :visible-layers="visibleGeoItemLayers"
                :update-failed="mapElem.mapSelectedElemUpdateFailed"
                :create-failed="mapElem.mapSelectedElemCreateFailed"
                :fetch-geo-item-failed="mapElem.getGeoItemByIdFailed"
                @create="createMapSelectedElem"
                @update="updateMapSelectedElem"
                @confirm-delete="confirmDeleteMapSelectedElem"
                @show-image="showImage"
                @area-movie-search="doAreaMovieSearch"
                @play-movie="playMapSelectedElem"
                @edit-movie-tag="startEditMovieTag"
                @component-resized="onMapSelectedElemAreaSizeChange"
                @show-previous-geo-item="showPreviousGeoItem"
                @show-next-geo-item="showNextGeoItem"
                @fetch-geo-item="getGeoItemById"
              />
            </keep-alive>
          </div>

          <TopDebugComponent
            v-if="hasRoleSuperAdmin && isMapReady"
            :ref-extreme-map="refExtremeMap!"
            :cars="carState.cars"
            style="margin:24px 16px;"
          />
        </div>
        <MultipaneResizer />
        <div class="pane-right" ref="refPaneRight" :style="{
            'min-width': styles.paneStyles.sideMinWidth,
            'max-width': styles.paneStyles.sideMaxWidth,
          }"
        >
          <MovieSearchResultPane />
        </div>
      </Multipane>
    </div>

    <MoviePlayer
      v-show="showMoviePlayerFullMode"
      ref="refMoviePlayer"
      :movie-lists="moviePlayerState.moviePlayerMovieLists"
      :movie-list-id="moviePlayerState.moviePlayerFullMovieListId"
      :extreme-map-essentials="state.extremeMapEssentials"
      :parent-geo-items-meta="geoItemsMeta"
      @close="closeMoviePlayer"
      @switch-to-compare-mode="switchMoviePlayerToCompareMode"
    />
    <MoviePlayerCompareMode
      v-show="showMoviePlayerCompareMode"
      ref="refMoviePlayerCompareMode"
      :movie-lists="moviePlayerState.moviePlayerMovieLists"
      :movie-list-id="moviePlayerState.moviePlayerCompMovieListId"
      :extreme-map-essentials="state.extremeMapEssentials"
      :parent-geo-items-meta="geoItemsMeta"
      :parent-movie-layer-meta="movieLayerMeta"
      :parent-movie-search="movieSearchState"
      @close="closeMoviePlayer"
      @switch-to-full-mode="switchMoviePlayerToFullMode"
    />

    <LoadWaitSpinner v-if="state.ui.waitSpinner.show" :message="state.ui.waitSpinner.msg" />

    <Modal v-if="state.ui.simpleModal.show" :title="state.ui.simpleModal.title"
      @close="state.ui.simpleModal.onClose()"
      @dismiss="state.ui.simpleModal.onDismiss()"
    >
      <template #body>{{state.ui.simpleModal.message}}</template>
    </Modal>

    <ModalImageView
      v-if="imageViewModal.show"
      :title="imageViewModal.title"
      :image-urls="imageViewModal.imageUrls"
      :download-filename-template="imageViewModal.downloadFilenameTemplate"
      :initial-index="imageViewModal.initialIndex"
      @close="closeImageModal"
    />

    <DeleteConfirmModal />
    <MovieTagSearchModal />
    <MovieTagEditModal />
  </div>
</template>

<script setup lang="ts">
import TopLeftPane from 'src/components/Top/TopLeftPane.vue';
import MapElemInfoNone from 'src/components/Top/mapElemInfoComponents/MapElemInfoNone.vue';
import MapElemInfoNew from 'src/components/Top/mapElemInfoComponents/MapElemInfoNew.vue';
import MapElemInfoCar from 'src/components/Top/mapElemInfoComponents/MapElemInfoCar.vue';
import MapElemInfoMovie from 'src/components/Top/mapElemInfoComponents/MapElemInfoMovie.vue';
import MapElemInfoComment from 'src/components/Top/mapElemInfoComponents/MapElemInfoComment.vue';
import MapElemInfoCommentContainer from 'src/components/Top/mapElemInfoComponents/MapElemInfoCommentContainer.vue';
import MapElemInfoLandApEmergency from 'src/components/Top/mapElemInfoComponents/MapElemInfoLandApEmergency.vue';
import MapElemInfoJunkaiTenkenReportTenkenData
  from 'src/components/Top/mapElemInfoComponents/MapElemInfoJunkaiTenkenReportTenkenData.vue';
import MapElemInfoLandAiDetections from 'src/components/Top/mapElemInfoComponents/MapElemInfoLandAiDetections.vue';
import TopDebugComponent from 'src/components/Top/TopDebugComponent/index.vue';
import MovieSearchResultPane from 'src/components/Top/MovieSearchResultPane.vue';
import MovieSearchBar from 'src/components/Top/MovieSearchBar.vue';
import DeleteConfirmModal from 'src/components/Top/DeleteConfirmModal.vue';
import MovieTagSearchModal from 'src/components/Top/MovieTagSearchModal.vue';
import MovieTagEditModal from 'src/components/Top/MovieTagEditModal.vue';
import ExtremeMap from 'src/components/lib/ExtremeMap/index.vue';
import MoviePlayer from 'src/components/lib/MoviePlayer/index.vue';
import MoviePlayerCompareMode from 'src/components/lib/MoviePlayerCompareMode/index.vue';
import { provideTopView } from 'src/composables/useTopView';

// Top.vue は provider 本体。全ドメインストアを生成・provide し、戻り値で受け取る。
// （provide/inject は親→子のため、provider 自身は inject できず戻り値経由で使う。
//  子コンポーネントは useXxxStore() で inject する。）
const {
  shell,
  mapSelection,
  movieSearch,
  imageModal,
  geoItemLayers,
  movieTagEdit,
  moviePlayer,
} = provideTopView();

// 画面共有状態（レイアウト・refs・マスタ・車両）
const {
  state,
  geoItemsMeta,
  movieLayerMeta,
  styles,
  refExtremeMap,
  refGeneralSearchBarRow,
  refPaneLeft,
  refPaneCenter,
  refMapTopMiscBar,
  refMapTopMiscBarLeft,
  refMapTopMiscBarRight,
  refMapSelectedElemInfoArea,
  refPaneRight,
  dtFormat,
  hasRoleSuperAdmin,
  isMapReady,
  paneResizeStopped,
  carState,
} = shell;

// 地図選択
const {
  mapElem,
  currentElemInfoComponent,
  onClickMap,
  onMapItemClicked,
  onMapAllDeselected,
  createMapSelectedElem,
  updateMapSelectedElem,
  confirmDeleteMapSelectedElem,
  playMapSelectedElem,
  onMapSelectedElemAreaSizeChange,
  showPreviousGeoItem,
  showNextGeoItem,
  getGeoItemById,
} = mapSelection;

// 動画検索
const {
  movieSearch: movieSearchState,
  doAreaMovieSearch,
} = movieSearch;

// 画像モーダル
const { imageViewModal, showImage, closeImageModal } = imageModal;

// 動画プレイヤー
const {
  moviePlayerState,
  refMoviePlayer,
  refMoviePlayerCompareMode,
  showMoviePlayerFullMode,
  showMoviePlayerCompareMode,
  closeMoviePlayer,
  switchMoviePlayerToCompareMode,
  switchMoviePlayerToFullMode,
} = moviePlayer;

// ジオアイテムレイヤー
const {
  visibleGeoItemLayers,
  onMapZoomChanged,
} = geoItemLayers;

// 動画タグ編集
const { startEditMovieTag } = movieTagEdit;

// <component :is="..."> の文字列解決に必要なコンポーネントのみ登録する
// （import してタグ名で使用するコンポーネントは script setup が自動解決する）
defineOptions({
  components: {
    MapElemInfoNone,
    MapElemInfoNew,
    MapElemInfoCar,
    MapElemInfoMovie,
    MapElemInfoComment,
    MapElemInfoCommentContainer,
    MapElemInfoLandApEmergency,
    MapElemInfoJunkaiTenkenReportTenkenData,
    MapElemInfoLandAiDetections,
  },
});
</script>

<style lang="scss" scoped>
.container-fluid {
  position: relative;
  @media only screen and (max-width: 670px) {
    padding-right: 0;
  }
}

.row1 {
  padding: 8px 8px 16px;
}

.row2 {
  margin: 0 auto;
  padding: 4px 4px 12px;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;

  .vertical-panes {
    width: 100%;
  }
  .multipane-resizer {
    margin: 0 0 0 4px;
    left: 0;
    position: relative;
    &:before {
      display: block;
      content: "";
      width: 4px;
      height: 40px;
      position: absolute;
      top: -10px;
      left: 1px;
      border-left: 1px solid #ccc;
      border-right: 1px solid #ccc;
    }
    &:hover {
      &:before {
        border-color: #999;
      }
    }
  }
  .pane-left {
    margin: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    z-index: 999;
  }
  .pane-center {
    position: relative;

    .misc-info {
      position: relative;
    }
    .misc-info-left {
      position: absolute;
      left: 2px;
      bottom: 0;
      font-size: 0.9em;
    }
    .misc-info-right {
      position: absolute;
      right: 0;
      bottom: 0;
      font-size: 0.9em;
      @media only screen and (max-width: 650px) {
        font-size: 0.8em;
      }

      .car-info-row {
        .car-info-data-age {
          float: right;
        }
        @media only screen and (max-width: 1100px) {
          .car-info-data-age {
            float: none;
          }
        }
      }
      .spacer-row {
        width: 1px;
      }
    }

    .extreme-map {
      border: 1px solid #dddddd;
    }
  }

  .pane-right {
    margin: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}

</style>
